'use client';

// ChatInterface - Consumer chat interface for attendees
// Story 4.2: Chat Interface for Asking Questions
// Story 4.3: Real-Time Bot Responses
// Story 4.4: Attendee Feedback on Answer Quality
// Story 4.5: Mobile-Optimized Bot Interface

import { useRef, useEffect, useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { useTranslations } from 'next-intl';
import { ChatInput, ChatBubble, type Message } from '@boty/ui';
import { ChatMessage } from './ChatMessage';
import { SuggestedQuestions } from './SuggestedQuestions';
import { IntakeQuestionsFlow } from './IntakeQuestionsFlow';
import type { useIntakeFlow } from './useIntakeFlow';
import { ExternalLinkDialog, useExternalLinkDialog } from './ExternalLinkDialog';
import { CreatorMessageButton } from './CreatorMessageButton';
import { CreatorMessageFlow } from './CreatorMessageFlow';
import { useKeyboardVisibility } from './useKeyboardVisibility';
import type { FeedbackRating, FeedbackEntry } from './types';
import type { useCreatorMessage } from './useCreatorMessage';

// Map server error messages to specific i18n keys
function getErrorMessage(
  error: Error,
  t: (key: string) => string
): string {
  const msg = error.message.toLowerCase();
  // Use specific phrases — bare "rate" matched substrings like "generateChatResponse".
  if (msg.includes('rate limit') || msg.includes('too many')) return t('error.rateLimited');
  if (msg.includes('not found')) return t('error.botNotFound');
  if (msg.includes('no content')) return t('error.noContent');
  if (msg.includes('network') || msg.includes('fetch')) return t('error.networkError');
  return t('error.sendFailed');
}

interface ChatInterfaceProps {
  botName: string;
  customWelcomeMessage?: string | null;
  messages: Message[];
  onSendMessage: (message: string) => void;
  isLoading?: boolean;
  error?: Error | null;
  feedbackMap?: Record<string, FeedbackEntry>;
  onFeedbackSubmit?: (
    messageId: string,
    rating: FeedbackRating,
    comment?: string
  ) => void;
  /** Called once when user scrolls to bottom of conversation */
  onScrollToBottom?: () => void;
  /** Suggested questions to display when chat is empty (Story 4.10) */
  suggestedQuestions?: string[];
  /** Whether suggested questions are still loading */
  suggestedQuestionsLoading?: boolean;
  /** Intake questions flow (Story 7.6) */
  intakeFlow?: ReturnType<typeof useIntakeFlow> | undefined;
  creatorName?: string;
  /** Creator message hook return value (Story 4.11) */
  creatorMessage?: ReturnType<typeof useCreatorMessage>;
}

export function ChatInterface({
  botName,
  customWelcomeMessage,
  messages,
  onSendMessage,
  isLoading = false,
  error,
  feedbackMap,
  onFeedbackSubmit,
  onScrollToBottom,
  suggestedQuestions = [],
  suggestedQuestionsLoading = false,
  intakeFlow,
  creatorName,
  creatorMessage,
}: ChatInterfaceProps) {
  const t = useTranslations('consumer.bot');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollToBottomFired = useRef(false);

  // Track which suggested questions have been used
  const [usedQuestions, setUsedQuestions] = useState<Set<string>>(new Set());
  const allQuestionsUsed = suggestedQuestions.length > 0 && suggestedQuestions.every((q) => usedQuestions.has(q));

  const handleSuggestedQuestionSelect = useCallback((question: string) => {
    setUsedQuestions((prev) => new Set(prev).add(question));
    onSendMessage(question);
  }, [onSendMessage]);

  // Welcome message: use custom if provided, fall back to i18n default
  const welcomeMessage = customWelcomeMessage || t('welcomeMessage', { botName });

  // External link confirmation dialog
  const { state: linkDialogState, openDialog: openLinkDialog, closeDialog: closeLinkDialog } = useExternalLinkDialog();

  // Keyboard visibility for mobile scroll handling
  const { isKeyboardOpen } = useKeyboardVisibility();

  // Scroll to bottom helper
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  }, []);

  // Auto-scroll to bottom when messages change or streaming updates content
  // Use 'auto' (instant) during streaming to avoid queued smooth-scroll animations
  const isAnyStreaming = messages.some((m) => m.isStreaming);
  useEffect(() => {
    scrollToBottom(isAnyStreaming ? 'auto' : 'smooth');
  }, [messages, isAnyStreaming, scrollToBottom]);

  // Scroll to bottom when keyboard opens to ensure input stays visible
  useEffect(() => {
    if (isKeyboardOpen) {
      // Delay slightly to allow keyboard animation to complete
      const timer = setTimeout(() => {
        scrollToBottom('smooth');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isKeyboardOpen, scrollToBottom]);

  // Story 6.11: Detect scroll to bottom for engagement trigger
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !onScrollToBottom || scrollToBottomFired.current) return;

    const handleScroll = () => {
      if (scrollToBottomFired.current) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      // Consider "at bottom" when within 50px of the bottom
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        scrollToBottomFired.current = true;
        onScrollToBottom();
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [onScrollToBottom]);

  // Handle input focus - scroll to ensure visibility on mobile
  const handleInputFocus = useCallback(() => {
    // On mobile, scroll input into view when focused
    setTimeout(() => {
      inputContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
  }, []);

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Chat messages area - responsive padding for mobile */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: { xs: 1, sm: 2, md: 3 },
          py: 2,
          bgcolor: 'grey.50',
        }}
      >
        {/* Welcome message from bot - render as markdown so links are clickable */}
        <ChatBubble sender="bot" markdown onLinkClick={openLinkDialog} sx={{ mb: 2 }}>
          {welcomeMessage}
        </ChatBubble>

        {/* Story 4.10: Suggested questions shown in chat area only before first message */}
        {messages.length === 0 && suggestedQuestions.length > 0 && (
          <SuggestedQuestions
            questions={suggestedQuestions}
            isLoading={suggestedQuestionsLoading}
            onSelect={handleSuggestedQuestionSelect}
          />
        )}

        {/* Conversation messages - uses ChatMessage wrapper for timestamp support */}
        {messages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            {...(feedbackMap && message.role === 'assistant'
              ? { feedbackRating: feedbackMap[message.id]?.rating }
              : {})}
            {...(onFeedbackSubmit && message.role === 'assistant'
              ? { onFeedbackSubmit }
              : {})}
            onLinkClick={openLinkDialog}
          />
        ))}

        {/* Intake Questions Flow (Story 7.6) */}
        {intakeFlow && (
          <IntakeQuestionsFlow
            flow={intakeFlow}
            creatorName={creatorName || botName}
          />
        )}

        {/* Error banner below messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {getErrorMessage(error, t)}
          </Alert>
        )}

        {/* Auto-scroll anchor */}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input area - fixed at bottom with safe-area support */}
      <Box
        ref={inputContainerRef}
        onFocus={handleInputFocus}
        sx={{
          pt: 1,
          pb: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
          <ChatInput
            onSend={onSendMessage}
            placeholder={t('inputPlaceholder')}
            sendAriaLabel={t('send')}
            isLoading={isLoading}
            maxLength={500}
            showCounterThreshold={400}
          />
        </Box>

        {/* Story 4.10: Persistent suggested questions below input — hidden once all used */}
        {suggestedQuestions.length > 0 && !allQuestionsUsed && messages.length > 0 && (
          <Box sx={{ pt: 1 }}>
            <SuggestedQuestions
              questions={suggestedQuestions}
              onSelect={handleSuggestedQuestionSelect}
              usedQuestions={usedQuestions}
            />
          </Box>
        )}

        {/* Story 4.11: Creator message button above footer */}
        {creatorMessage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
            <CreatorMessageButton
              onClick={creatorMessage.open}
              hasSent={creatorMessage.hasSent}
            />
          </Box>
        )}
      </Box>

      {/* Story 4.11: Creator message flow dialog */}
      {creatorMessage && (
        <CreatorMessageFlow
          step={creatorMessage.step}
          data={creatorMessage.data}
          onUpdateData={creatorMessage.updateData}
          onGoToStep={creatorMessage.goToStep}
          onSubmit={creatorMessage.submit}
          onClose={creatorMessage.close}
          onReset={creatorMessage.reset}
        />
      )}

      {/* External link confirmation dialog */}
      <ExternalLinkDialog
        open={linkDialogState.open}
        url={linkDialogState.url}
        onClose={closeLinkDialog}
      />
    </Box>
  );
}
